// Self-service Account overview (Identity/Billing roadmap, Phase 2). Renders the caller's own account
// at the top of the Account view: identity + plan + billing status, a credits/usage summary, and the
// account's users. Read-only for now (edit + member management is a follow-up slice). Fails quiet: if
// the fetch errors (e.g. a member who somehow reaches it gets 403), the block is left empty so the
// preferences below still render. All model/text is written via textContent (never innerHTML).

import * as api from '../lib/api.js';
import { navigateTo } from './sidenav.js';
import { customSelect, type CSInstance } from '../lib/customSelect.js';

// Homepage options for the Your Users table — the pages a super user can set as a member's login landing.
// Grouped to mirror the left nav (One-Off Diagnosis + The CRO Zone); values are top-level view ids. Kept
// in sync with HOMEPAGE_VIEWS in the worker (apps/worker/src/api/account.ts). Default is 'home'.
const HOMEPAGE_GROUPS: { group: string; options: [string, string][] }[] = [
  { group: 'One-Off Diagnosis', options: [
    ['home', 'One-Off Diagnosis'], ['diagnostic', 'Quick Snapshot'], ['coaching', 'Coaching Summary'],
    ['cross-skill', 'Cross-Skill'], ['buyer-side', 'Buyer Signals'], ['meddpicc', 'MEDDPICC'],
  ] },
  { group: 'The CRO Zone', options: [
    ['bespoke-playbook', 'Benchmark'], ['trends', 'Trends'], ['team-trends', 'Average Scores'],
    ['compare', 'Compare'], ['history', 'History'],
  ] },
];
import {
  getClerkUserInfo,
  applyAvatar,
  uploadProfileImage,
  removeProfileImage,
  updateName,
  getSecurityInfo,
  signOutDevice,
  deleteAccount,
  type ClerkUserInfo,
} from '../lib/appClerk.js';

function h<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

/** A labelled key/value cell for the details grid. */
function field(label: string, value: string): HTMLElement {
  const cell = h('div', 'acct-field');
  cell.appendChild(h('div', 'acct-field-label', label));
  cell.appendChild(h('div', 'acct-field-value', value));
  return cell;
}

// Country dial codes for the phone field. Value = the stored dial code (some countries share one,
// e.g. +1); label carries the country name so it's findable. Sorted A-Z by country name (the "Code"
// placeholder stays first). Curated common-market set — add new entries in their alphabetical slot.
const DIAL_CODES: ReadonlyArray<{ code: string; label: string }> = [
  { code: '', label: 'Code' },
  { code: '+54', label: 'Argentina +54' },
  { code: '+61', label: 'Australia +61' },
  { code: '+43', label: 'Austria +43' },
  { code: '+32', label: 'Belgium +32' },
  { code: '+55', label: 'Brazil +55' },
  { code: '+1', label: 'Canada +1' },
  { code: '+86', label: 'China +86' },
  { code: '+420', label: 'Czechia +420' },
  { code: '+45', label: 'Denmark +45' },
  { code: '+358', label: 'Finland +358' },
  { code: '+33', label: 'France +33' },
  { code: '+49', label: 'Germany +49' },
  { code: '+30', label: 'Greece +30' },
  { code: '+852', label: 'Hong Kong +852' },
  { code: '+91', label: 'India +91' },
  { code: '+353', label: 'Ireland +353' },
  { code: '+972', label: 'Israel +972' },
  { code: '+39', label: 'Italy +39' },
  { code: '+81', label: 'Japan +81' },
  { code: '+52', label: 'Mexico +52' },
  { code: '+31', label: 'Netherlands +31' },
  { code: '+64', label: 'New Zealand +64' },
  { code: '+47', label: 'Norway +47' },
  { code: '+48', label: 'Poland +48' },
  { code: '+351', label: 'Portugal +351' },
  { code: '+966', label: 'Saudi Arabia +966' },
  { code: '+65', label: 'Singapore +65' },
  { code: '+27', label: 'South Africa +27' },
  { code: '+82', label: 'South Korea +82' },
  { code: '+34', label: 'Spain +34' },
  { code: '+46', label: 'Sweden +46' },
  { code: '+41', label: 'Switzerland +41' },
  { code: '+971', label: 'United Arab Emirates +971' },
  { code: '+44', label: 'United Kingdom +44' },
  { code: '+1', label: 'United States +1' },
];

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trialing: 'Free trial',
  pending: 'Awaiting activation',
  past_due: 'Payment overdue',
  canceled: 'Canceled',
  paused: 'Paused',
};

/** Fetch + paint the account overview. Left column (#account-overview) = identity/details; right
 *  column (#account-overview-right) = usage tiles + users. Called each time the Account view shows. */
/** The last successfully-loaded account (owner data), so the unified editor can include the account
 *  fields. null for members (403 on getAccount) or before it loads — the editor then shows the
 *  profile fields (name + photo) only. Set by paint(). */
let lastAccount: api.AccountSummary | null = null;

/** Profile + account block: a read-only display (photo, name, email) with ONE "Edit" button that
 *  expands a single form covering everything — photo, name, and (for owners) company / billing /
 *  phone — saved together. Rendered independently of the account API so it shows for every signed-in
 *  user; the account fields appear only once owner data has loaded. */
function renderProfileCard(): void {
  const root = document.getElementById('account-profile');
  if (!root) return;
  root.textContent = '';

  const profile = h('div', 'acct-profile');
  const pAvatar = h('span', 'acct-avatar');
  pAvatar.textContent = '·';
  const pText = h('div', 'acct-profile-text');
  const pName = h('div', 'acct-profile-name', 'Your profile');
  const pEmail = h('div', 'acct-profile-email');
  pText.appendChild(pName);
  pText.appendChild(pEmail);

  const actions = h('div', 'acct-profile-actions');
  actions.id = 'acct-profile-actions'; // paint() drops the status pill in here, below the Edit button
  const editBtn = h('button', 'react-nav acct-edit-trigger', 'Edit'); // matches the reaction-panel nav buttons
  editBtn.type = 'button';
  actions.appendChild(editBtn);

  profile.appendChild(pAvatar);
  profile.appendChild(pText);
  profile.appendChild(actions); // sits right after the name (see .acct-profile-text flex in CSS)
  root.appendChild(profile);
  const slot = h('div', 'acct-edit-slot');
  root.appendChild(slot);

  void getClerkUserInfo().then((info) => {
    applyAvatar(pAvatar, info);
    pName.textContent = info?.fullName || info?.firstName || 'Your profile';
    pEmail.textContent = info?.email || '';
  });

  // Edit toggles the editor open/closed; the button flips to "Close" while expanded. The display row
  // (avatar + name) stays visible as a header so the toggle is always reachable; the account grid below
  // is what the editor hides.
  let expanded = false;
  const collapse = (): void => {
    slot.textContent = '';
    const ov = document.getElementById('account-overview');
    if (ov) ov.hidden = false;
    editBtn.textContent = 'Edit';
    editBtn.classList.remove('is-open');
    expanded = false;
  };
  editBtn.addEventListener('click', () => {
    if (expanded) {
      collapse();
      return;
    }
    void getClerkUserInfo().then((info) => {
      openUnifiedEdit(slot, info, collapse);
      editBtn.textContent = 'Close';
      editBtn.classList.add('is-open');
      expanded = true;
    });
  });
}

/** The single expanded editor, fully native (no embedded Clerk widget): photo, name, and — for owners
 *  — company / billing / phone, saved together; then a sign-in & security block (connected account,
 *  active devices, delete account) rendered in our own look. No section headers, per the design. */
function openUnifiedEdit(slot: HTMLElement, info: ClerkUserInfo | null, onCancel: () => void): void {
  const a = lastAccount;
  const overview = document.getElementById('account-overview');
  if (overview) overview.hidden = true; // hide the read-only account grid while editing (no duplication)

  const panel = h('div', 'acct-edit-panel');

  // --- Fields (photo + name + owner's company/billing/phone) ---
  const form = h('div', 'acct-edit');

  // Photo: no preview circle (the header avatar already shows it) — just a Change/Remove control, left
  // aligned like the other fields, applied on Save.
  let pendingFile: File | null = null;
  let pendingRemove = false;
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = 'image/png,image/jpeg,image/webp,image/gif';
  fileInput.hidden = true;
  const changeBtn = h('button', 'acct-microbtn', 'Change photo');
  changeBtn.type = 'button';
  const removeBtn = h('button', 'acct-microbtn', 'Remove');
  removeBtn.type = 'button';
  removeBtn.hidden = !info?.hasImage;
  const photoStatus = h('span', 'acct-photo-msg');
  photoStatus.hidden = true;
  const photoBtns = h('div', 'acct-photo-actions');
  photoBtns.appendChild(changeBtn);
  photoBtns.appendChild(removeBtn);
  photoBtns.appendChild(photoStatus);
  const photoCol = h('div', 'acct-inp');
  photoCol.appendChild(h('span', 'acct-inp-label', 'Photo'));
  photoCol.appendChild(photoBtns);
  photoCol.appendChild(fileInput);
  form.appendChild(photoCol);
  changeBtn.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    const f = fileInput.files?.[0];
    if (!f) return;
    pendingFile = f;
    pendingRemove = false;
    removeBtn.hidden = false;
    photoStatus.textContent = 'New photo selected — Save to apply.';
    photoStatus.hidden = false;
  });
  removeBtn.addEventListener('click', () => {
    pendingFile = null;
    pendingRemove = true;
    removeBtn.hidden = true;
    photoStatus.textContent = 'Photo will be removed on Save.';
    photoStatus.hidden = false;
  });

  const currentName = info?.fullName || info?.firstName || '';
  const nameF = labeledInput('Your name', currentName, 'e.g. Avner Baruch');
  form.appendChild(nameF.wrap);

  let companyF: ReturnType<typeof labeledInput> | null = null;
  let bemailF: ReturnType<typeof labeledInput> | null = null;
  let phoneF: ReturnType<typeof phoneField> | null = null;
  if (a) {
    const companyVal = a.displayName && a.displayName !== a.ownerEmail ? a.displayName : '';
    companyF = labeledInput('Company name', companyVal, 'e.g. Acme Inc');
    bemailF = labeledInput('Billing email', a.billingEmail || '', 'billing@company.com');
    phoneF = phoneField(a.phoneCc, a.phone || '');
    form.appendChild(companyF.wrap);
    form.appendChild(bemailF.wrap);
    form.appendChild(phoneF.wrap);
  }

  const actionsRow = h('div', 'acct-edit-actions');
  const save = h('button', 'acct-textbtn', 'Save');
  save.type = 'button';
  const cancel = h('button', 'acct-textbtn acct-textbtn--muted', 'Cancel');
  cancel.type = 'button';
  const msg = h('p', 'acct-edit-msg');
  msg.hidden = true;
  actionsRow.appendChild(save);
  actionsRow.appendChild(cancel);
  form.appendChild(actionsRow);
  form.appendChild(msg);
  panel.appendChild(form);

  cancel.addEventListener('click', () => onCancel());
  save.addEventListener('click', () => {
    save.disabled = true;
    cancel.disabled = true;
    save.textContent = 'Saving…';
    msg.hidden = true;
    void (async () => {
      let ok = true;
      try {
        if (pendingFile) ok = (await uploadProfileImage(pendingFile)) && ok;
        else if (pendingRemove) ok = (await removeProfileImage()) && ok;
        const nameVal = nameF.input.value.trim();
        if (nameVal && nameVal !== currentName) ok = (await updateName(nameVal)) && ok;
        if (a && companyF && bemailF && phoneF) {
          await api.updateAccount({
            display_name: companyF.input.value.trim(),
            billing_email: bemailF.input.value.trim(),
            phone_cc: phoneF.cc.value,
            phone: phoneF.num.value.trim(),
          });
        }
      } catch {
        ok = false;
      }
      if (ok) {
        renderAccount();
        return;
      }
      save.disabled = false;
      cancel.disabled = false;
      save.textContent = 'Save';
      msg.textContent = 'Some changes could not be saved. Check the details and try again.';
      msg.hidden = false;
    })();
  });

  // --- Sign-in & security (native), separated by a divider, no header ---
  const secBlock = h('div', 'acct-sec');
  panel.appendChild(secBlock);
  renderSecurity(secBlock);

  slot.textContent = '';
  slot.appendChild(panel);
  nameF.input.focus();
}

/** A key/value row for the security block. */
function securityRow(key: string, value: Node): HTMLElement {
  const row = h('div', 'acct-sec-row');
  row.appendChild(h('div', 'acct-sec-k', key));
  const v = h('div', 'acct-sec-v');
  v.appendChild(value);
  row.appendChild(v);
  return row;
}

/** Native sign-in & security: connected account, active devices (sign out), delete account. Fetched
 *  from Clerk; degrades to hidden if the data can't be read. */
function renderSecurity(root: HTMLElement): void {
  root.textContent = '';
  root.appendChild(h('p', 'acct-sec-loading', 'Loading sign-in & security…'));
  void getSecurityInfo().then((info) => {
    root.textContent = '';
    if (!info) {
      root.hidden = true;
      return;
    }

    // Signed in with
    root.appendChild(securityRow('Signed in with', document.createTextNode(info.connectedLabel || info.email || 'This account')));

    // Active devices
    if (info.devices.length) {
      const list = h('div', 'acct-sec-devices');
      for (const d of info.devices) {
        const item = h('div', 'acct-sec-device');
        const txt = h('div', 'acct-sec-device-txt');
        const title = h('div', 'acct-sec-device-title', d.title);
        if (d.current) title.appendChild(h('span', 'acct-sec-badge', 'This device'));
        txt.appendChild(title);
        if (d.detail) txt.appendChild(h('div', 'acct-sec-device-detail', d.detail));
        item.appendChild(txt);
        if (!d.current) {
          const out = h('button', 'acct-microbtn', 'Sign out');
          out.type = 'button';
          out.addEventListener('click', () => {
            out.disabled = true;
            out.textContent = 'Signing out…';
            void signOutDevice(d.id).then((ok) => {
              if (ok) item.remove();
              else {
                out.disabled = false;
                out.textContent = 'Sign out';
              }
            });
          });
          item.appendChild(out);
        }
        list.appendChild(item);
      }
      root.appendChild(securityRow('Active devices', list));
    }

    // Delete account
    const delBtn = h('button', 'acct-microbtn acct-sec-danger', 'Delete account');
    delBtn.type = 'button';
    delBtn.addEventListener('click', () => {
      if (!window.confirm('Delete your account permanently? This cannot be undone.')) return;
      delBtn.disabled = true;
      delBtn.textContent = 'Deleting…';
      void deleteAccount().then((ok) => {
        if (ok) window.location.assign('/');
        else {
          delBtn.disabled = false;
          delBtn.textContent = 'Delete account';
        }
      });
    });
    root.appendChild(securityRow('Delete account', delBtn));
  });
}

export function renderAccount(): void {
  renderProfileCard(); // independent of the account fetch below, so it always shows
  const leftRoot = document.getElementById('account-overview');
  const rightRoot = document.getElementById('account-overview-right');
  const usersRoot = document.getElementById('account-users');
  if (!leftRoot || !rightRoot || !usersRoot) return;
  leftRoot.textContent = '';
  rightRoot.textContent = '';
  usersRoot.textContent = '';
  leftRoot.appendChild(h('p', 'acct-loading', 'Loading your account…'));
  api
    .getAccount()
    .then((a) => paint(leftRoot, rightRoot, usersRoot, a))
    .catch(() => {
      leftRoot.hidden = false; // in case a prior edit hid it (member editing their own name/photo)
      leftRoot.textContent = ''; // fail quiet — leave the Preferences below usable
      rightRoot.textContent = '';
      usersRoot.textContent = '';
    });
}

function paint(leftRoot: HTMLElement, rightRoot: HTMLElement, usersRoot: HTMLElement, a: api.AccountSummary): void {
  lastAccount = a; // let the unified editor include the account fields
  leftRoot.hidden = false; // in case a prior edit hid the read-only details
  leftRoot.textContent = '';
  rightRoot.textContent = '';
  usersRoot.textContent = '';

  const company = a.displayName && a.displayName !== a.ownerEmail ? a.displayName : a.ownerName || a.ownerEmail || 'Your account';
  const header = h('div', 'acct-head');
  header.appendChild(h('h2', 'acct-title', `Account Name: ${company}`));
  leftRoot.appendChild(header);

  // Status pill sits under the Edit button (in the profile card's action column), not next to the name.
  const profileActions = document.getElementById('acct-profile-actions');
  if (profileActions) {
    profileActions.querySelector('.acct-status')?.remove(); // no duplicate on re-render
    const statusWord = STATUS_LABEL[a.billingStatus] ?? a.billingStatus;
    profileActions.appendChild(h('span', `acct-status acct-status--${a.billingStatus}`, statusWord));
  }

  // Identity + plan. Row 1: Primary super user, Plan. Row 2: Billing email, Phone (2-column grid).
  const grid = h('div', 'acct-grid');
  if (a.ownerEmail) grid.appendChild(field('Primary super user', a.ownerName ? `${a.ownerName} · ${a.ownerEmail}` : a.ownerEmail));
  grid.appendChild(field('Plan', a.planTier));
  grid.appendChild(field('Billing email', a.billingEmail || 'Not set'));
  grid.appendChild(field('Phone', a.phone ? `${a.phoneCc ? a.phoneCc + ' ' : ''}${a.phone}` : 'Not set'));
  leftRoot.appendChild(grid);
  // (Editing is handled by the single "Edit" button in the profile block above → openUnifiedEdit.)

  // Credits / usage summary (right column)
  const rightCol = rightRoot;
  const usage = h('div', 'acct-usage');
  usage.appendChild(stat(String(a.callsTotal), 'Calls scored'));
  usage.appendChild(stat(a.billingStatus === 'active' ? '—' : (a.trialActive ? `${a.trialDaysLeft}d` : 'Ended'), 'Free trial left'));
  usage.appendChild(stat(String(a.granted), 'Calls granted'));
  const left = stat(String(a.creditsLeft), 'Calls left');
  if (a.creditsLeft <= 0 && a.billingStatus !== 'active') left.classList.add('acct-stat--low');
  usage.appendChild(left);
  rightCol.appendChild(usage);

  // Upgrade nudge when not active: point to Billing & Plans, where self-serve checkout buys a call pack.
  if (a.billingStatus !== 'active') {
    const note = h('div', 'acct-note');
    note.appendChild(document.createTextNode('Need more calls or to activate a plan? '));
    const link = h('button', 'acct-link', 'Go to Billing & Plans');
    link.type = 'button';
    link.addEventListener('click', () => navigateTo('billing'));
    note.appendChild(link);
    rightCol.appendChild(note);
  }

  // Users — in the right column, under the tiles + note (wrapped so a narrow column scrolls the table
  // instead of the page).
  const usersWrap = h('div', 'acct-users');
  const uh = h('div', 'acct-users-head');
  uh.appendChild(h('h3', 'acct-sub', 'Your users'));
  uh.appendChild(h('span', 'acct-count', `${a.members.length} user${a.members.length === 1 ? '' : 's'}`));
  usersWrap.appendChild(uh);

  const table = h('table', 'acct-table');
  const thead = h('thead');
  const htr = h('tr');
  ['User', 'Role', 'Status', 'Calls', 'Homepage', ''].forEach((c) => htr.appendChild(h('th', undefined, c)));
  thead.appendChild(htr);
  table.appendChild(thead);
  const tbody = h('tbody');
  for (const m of a.members) {
    const isOwner = m.role === 'owner' || (a.ownerEmail !== null && m.email === a.ownerEmail);
    const tr = h('tr');
    const who = h('td');
    who.appendChild(h('div', 'acct-user-name', m.name || m.email));
    if (m.name) who.appendChild(h('div', 'acct-user-email', m.email));
    tr.appendChild(who);
    tr.appendChild(h('td', undefined, m.role === 'member' ? 'Member' : 'Super user'));
    tr.appendChild(h('td', undefined, m.status === 'paused' ? 'Paused' : 'Active'));
    tr.appendChild(h('td', 'acct-num', String(m.calls)));

    // Homepage — the page this user lands on at login. Settable for every user (the owner included),
    // grouped by the One-Off Diagnosis and CRO Zone nav groups. Default is One-Off Diagnosis ('home').
    const hpCell = h('td', 'acct-homepage');
    const hpCs = customSelect({
      value: m.homepage || 'home',
      options: HOMEPAGE_GROUPS.flatMap((grp) => [
        { id: `__h:${grp.group}`, name: grp.group, header: true },
        ...grp.options.map(([val, label]) => ({ id: val, name: label })),
      ]),
      ariaLabel: 'Homepage',
      className: 'acct-homepage-select',
      onChange: (v) => memberActionCs(hpCs, () => api.setAccountMember(m.email, 'set_homepage', undefined, v)),
    });
    hpCell.appendChild(hpCs.root);
    tr.appendChild(hpCell);

    // Actions — the account's primary super user is protected (no controls, can't be removed, which is
    // what guarantees every account keeps at least one super user); everyone else gets role + pause +
    // remove. "Owner" is never shown to customers — that word is reserved for the platform (Avner).
    const acts = h('td', 'acct-actions');
    if (isOwner) {
      const chip = h('span', 'acct-lock', 'Primary');
      chip.title = 'The account’s primary super user — can’t be removed, so the account always keeps a super user.';
      acts.appendChild(chip);
    } else {
      const roleCs = customSelect({
        value: m.role === 'member' ? 'member' : 'owner',
        options: [{ id: 'owner', name: 'Super user' }, { id: 'member', name: 'Member' }],
        ariaLabel: 'Role',
        className: 'acct-role-select',
        onChange: (v) => memberActionCs(roleCs, () => api.setAccountMember(m.email, 'set_role', v as 'owner' | 'member')),
      });
      acts.appendChild(roleCs.root);
      const pauseBtn = h('button', 'acct-mini-btn', m.status === 'paused' ? 'Resume' : 'Pause');
      pauseBtn.type = 'button';
      pauseBtn.addEventListener('click', () => memberAction(pauseBtn, () => api.setAccountMember(m.email, m.status === 'paused' ? 'resume' : 'pause')));
      acts.appendChild(pauseBtn);
      if (a.invitesEnabled) {
        const rmBtn = h('button', 'acct-mini-btn acct-remove-btn', 'Remove');
        rmBtn.type = 'button';
        rmBtn.addEventListener('click', () => {
          if (!window.confirm(`Remove ${m.email}? They will lose access to this account.`)) return;
          memberAction(rmBtn, () => api.removeMember(m.email));
        });
        acts.appendChild(rmBtn);
      }
    }
    tr.appendChild(acts);
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  const scroll = h('div', 'acct-table-scroll');
  scroll.appendChild(table);
  usersWrap.appendChild(scroll);

  // Add a new user: a self-service invite form when invites are wired (Cloudflare Access configured),
  // otherwise the original "contact us" note.
  if (a.invitesEnabled) usersWrap.appendChild(inviteForm());
  else usersWrap.appendChild(h('p', 'acct-users-note', 'Need to add a new user? Contact us and we will set it up. Self-service invites are coming soon.'));
  usersRoot.appendChild(usersWrap); // Your Users renders below the Teams block in the right column.
}

const INVITE_ERR: Record<string, string> = {
  invalid_email: 'Enter a valid email address.',
  already_member: 'That email is already on this account.',
  email_taken: 'That email is already registered to another account.',
  access_failed: 'Couldn’t update the login allowlist just now — please try again.',
  invites_disabled: 'Invites aren’t enabled for this account.',
  network: 'Something went wrong — please try again.',
};
const invErr = (code?: string): string => INVITE_ERR[code ?? 'network'] ?? 'Something went wrong — please try again.';

/** The "invite a teammate" form: email + role, and a status line. On success it repaints Your Users so
 *  the new member appears. Role defaults to Member; a super user can grant Super user here. */
function inviteForm(): HTMLElement {
  const wrap = h('div', 'acct-invite');
  wrap.appendChild(h('h4', 'acct-invite-h', 'Add a teammate'));
  const row = h('div', 'acct-invite-row');

  const email = document.createElement('input');
  email.type = 'email';
  email.placeholder = 'name@company.com';
  email.className = 'acct-inp-field acct-invite-email';
  email.setAttribute('aria-label', 'Teammate email');

  const roleCs = customSelect({
    value: 'member',
    options: [{ id: 'member', name: 'Member' }, { id: 'owner', name: 'Super user' }],
    ariaLabel: 'Role',
    className: 'acct-role-select',
    onChange: () => { /* read at submit */ },
  });

  const btn = h('button', 'gold-btn acct-invite-btn', 'Invite');
  btn.type = 'button';
  const msg = h('span', 'acct-invite-msg', '');

  const submit = (): void => {
    const e = email.value.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { msg.className = 'acct-invite-msg is-err'; msg.textContent = invErr('invalid_email'); return; }
    btn.disabled = true; email.disabled = true; roleCs.setDisabled(true);
    msg.className = 'acct-invite-msg'; msg.textContent = 'Inviting…';
    const reenable = (): void => { btn.disabled = false; email.disabled = false; roleCs.setDisabled(false); };
    void api.inviteMember(e, roleCs.value as 'owner' | 'member')
      .then((r) => {
        if (r.ok) renderAccount(); // repaint — the new member row appears, form resets
        else { msg.className = 'acct-invite-msg is-err'; msg.textContent = invErr(r.error); reenable(); }
      })
      .catch(() => { msg.className = 'acct-invite-msg is-err'; msg.textContent = invErr('network'); reenable(); });
  };
  btn.addEventListener('click', submit);
  email.addEventListener('keydown', (ev) => { if ((ev as KeyboardEvent).key === 'Enter') submit(); });

  row.append(email, roleCs.root, btn);
  wrap.append(row, msg);
  wrap.appendChild(h('p', 'acct-invite-note', 'They’ll be added to the login allowlist and can sign in with this email. You can set their Homepage and role above once they appear.'));
  return wrap;
}

/** Disable the control while its member action runs, then re-fetch + repaint (reflects success, or
 *  reverts an optimistic select on failure — the server enforces the guards). */
function memberAction(el: HTMLButtonElement | HTMLSelectElement, fn: () => Promise<unknown>): void {
  el.disabled = true;
  fn()
    .then(() => renderAccount())
    .catch(() => renderAccount());
}

/** memberAction for a custom-select handle (no native .disabled). Repaint reflects server truth. */
function memberActionCs(cs: CSInstance, fn: () => Promise<unknown>): void {
  cs.setDisabled(true);
  fn()
    .then(() => renderAccount())
    .catch(() => renderAccount());
}

function labeledInput(label: string, value: string, placeholder: string): { wrap: HTMLElement; input: HTMLInputElement } {
  const wrap = h('label', 'acct-inp');
  wrap.appendChild(h('span', 'acct-inp-label', label));
  const input = document.createElement('input');
  input.type = 'text';
  input.value = value;
  input.placeholder = placeholder;
  input.className = 'acct-inp-field';
  wrap.appendChild(input);
  return { wrap, input };
}

/** Phone field: a country dial-code dropdown + a number input. Round-trips the two stored columns. */
function phoneField(currentCc: string | null, currentNum: string): { wrap: HTMLElement; cc: HTMLSelectElement; num: HTMLInputElement } {
  const wrap = h('label', 'acct-inp');
  wrap.appendChild(h('span', 'acct-inp-label', 'Phone'));
  const row = h('div', 'acct-phone-row');
  const cc = document.createElement('select');
  cc.className = 'acct-cc-select';
  let matched = false;
  for (const d of DIAL_CODES) {
    const o = document.createElement('option');
    o.value = d.code;
    o.textContent = d.label;
    if (!matched && d.code && d.code === currentCc) {
      o.selected = true;
      matched = true;
    }
    cc.appendChild(o);
  }
  const num = document.createElement('input');
  num.type = 'tel';
  num.value = currentNum;
  num.placeholder = '50 123 4567';
  num.className = 'acct-inp-field acct-phone-num';
  row.appendChild(cc);
  row.appendChild(num);
  wrap.appendChild(row);
  return { wrap, cc, num };
}

function stat(n: string, label: string): HTMLElement {
  const s = h('div', 'acct-stat');
  s.appendChild(h('div', 'acct-stat-n', n));
  s.appendChild(h('div', 'acct-stat-l', label));
  return s;
}
